import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Printer, FileDown, ClipboardList, ListOrdered } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROUND_LABELS = { 1: "Eliminatória", 2: "Físico", 3: "Coreografia", 4: "Comparação" };

export default function OperatorPrints() {
  const { currentId, current } = useCompetition();
  const [cats, setCats] = useState([]);
  const [judges, setJudges] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [round, setRound] = useState(1);
  const [judgeId, setJudgeId] = useState("");
  const [startTime, setStartTime] = useState("18:00");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!currentId) return;
    setError("");
    Promise.all([
      api.get(`/competitions/${currentId}/categories`),
      api.get(`/competitions/${currentId}/judges`),
    ])
      .then(([c, j]) => {
        setCats(c.data);
        setCategoryId(c.data[0]?.id || "");
        setJudges(j.data);
        setJudgeId(j.data[0]?.id || "");
      })
      .catch((e) => setError(extractErrorMessage(e)));
    if (current?.horario_inicio) setStartTime(current.horario_inicio);
  }, [currentId, current]);

  const openPdf = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const blankUrl = `${BACKEND}/api/export/sumula-blank.pdf?competition_id=${currentId}&category_id=${categoryId}&round_num=${round}`;
  const filledUrl = `${BACKEND}/api/export/sumula.pdf?competition_id=${currentId}&category_id=${categoryId}&round_num=${round}&judge_id=${judgeId}`;
  const backstageUrl = `${BACKEND}/api/export/backstage.pdf?competition_id=${currentId}&start_time=${encodeURIComponent(startTime)}`;

  return (
    <div data-testid="operator-prints-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-1">
            <Printer size={12} /> Documentos oficiais
          </div>
          <h1 className="font-display text-5xl font-black tracking-tight">IMPRESSÕES</h1>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            Cada impressão é registrada no log de atividades com data e hora.
          </div>
        </div>
        <CompetitionSelector />
      </div>

      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-4">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6 border-l-4 border-[#C9A84C]">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList size={18} className="text-[#C9A84C]" />
            <div className="font-display font-black text-2xl tracking-tight">SÚMULAS</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="md:col-span-2">
              <label className="overline block mb-2">Categoria</label>
              <select data-testid="print-categoria" className="input-field" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Selecione...</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Round</label>
              <select data-testid="print-round" className="input-field" value={round} onChange={(e) => setRound(parseInt(e.target.value))}>
                {[1, 2, 3, 4].map((r) => <option key={r} value={r}>R{r} — {ROUND_LABELS[r]}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Árbitro (súmula preenchida)</label>
              <select data-testid="print-arbitro" className="input-field" value={judgeId} onChange={(e) => setJudgeId(e.target.value)}>
                <option value="">Selecione...</option>
                {judges.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              data-testid="print-sumula-blank"
              className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
              disabled={!currentId || !categoryId}
              onClick={() => openPdf(blankUrl)}
            >
              <Printer size={14} /> SÚMULA EM BRANCO
            </button>
            <button
              data-testid="print-sumula-filled"
              className="btn-ghost flex-1 inline-flex items-center justify-center gap-2"
              disabled={!currentId || !categoryId || !judgeId}
              onClick={() => openPdf(filledUrl)}
            >
              <FileDown size={14} /> Súmula preenchida
            </button>
          </div>
        </div>

        <div className="card p-6 border-l-4 border-white/20">
          <div className="flex items-center gap-2 mb-4">
            <ListOrdered size={18} className="text-[#C9A84C]" />
            <div className="font-display font-black text-2xl tracking-tight">ROTEIRO / FICHAS DE CHAMADA</div>
          </div>
          <div className="text-xs text-[var(--text-secondary)] mb-4">
            Ordem de entrada em palco de todas as categorias, com lista de atletas para o backstage.
          </div>
          <div className="mb-4">
            <label className="overline block mb-2">Horário de início</label>
            <input data-testid="print-start-time" type="time" className="input-field w-40" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <button
            data-testid="print-backstage"
            className="btn-primary inline-flex items-center gap-2"
            disabled={!currentId}
            onClick={() => openPdf(backstageUrl)}
          >
            <Printer size={14} /> IMPRIMIR ROTEIRO (PDF)
          </button>
        </div>
      </div>
    </div>
  );
}
